/**
 * Chain Rosary Service — Firestore CRUD for rosary participants.
 */

import {
  collection, doc, addDoc, getDocs, updateDoc, deleteDoc,
  serverTimestamp, query, orderBy, Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';

export interface RosaryParticipant {
  id:                  string;
  name:                string;
  countryCode:         string;
  phone:               string;
  timeIST:             string; // "HH:MM" 24-hour IST
  callMessage:         string;
  notificationEnabled: boolean;
  createdAt:           Date | null;
}

export interface CreateParticipantInput {
  name:                string;
  countryCode:         string;
  phone:               string;
  timeIST:             string;
  callMessage:         string;
  notificationEnabled: boolean;
}

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  return null;
}

function docToParticipant(data: Record<string, unknown>, id: string): RosaryParticipant {
  return {
    id,
    name:                (data.name                as string)  ?? '',
    countryCode:         (data.countryCode         as string)  ?? '+91',
    phone:               (data.phone               as string)  ?? '',
    timeIST:             (data.timeIST             as string)  ?? '00:00',
    callMessage:         (data.callMessage         as string)  ?? 'Angels Army reminder call for Chain Rosary',
    notificationEnabled: (data.notificationEnabled as boolean) ?? false,
    createdAt:           toDate(data.createdAt),
  };
}

export async function getParticipants(): Promise<RosaryParticipant[]> {
  const snap = await getDocs(
    query(collection(db, 'rosaryParticipants'), orderBy('createdAt', 'asc'))
  );
  return snap.docs.map(d => docToParticipant(d.data() as Record<string, unknown>, d.id));
}

export async function addParticipant(input: CreateParticipantInput): Promise<string> {
  const ref = await addDoc(collection(db, 'rosaryParticipants'), {
    ...input,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateParticipant(
  id: string,
  fields: Partial<Omit<RosaryParticipant, 'id' | 'createdAt'>>,
): Promise<void> {
  await updateDoc(doc(db, 'rosaryParticipants', id), fields);
}

export async function deleteParticipant(id: string): Promise<void> {
  await deleteDoc(doc(db, 'rosaryParticipants', id));
}

// Current IST time as HH:MM string
export function getCurrentIST(): string {
  const now = new Date();
  const ist = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  return `${String(ist.getUTCHours()).padStart(2, '0')}:${String(ist.getUTCMinutes()).padStart(2, '0')}`;
}
